import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  Link,
} from '@mui/material';
import Refresh from '@mui/icons-material/Refresh';
import Search from '@mui/icons-material/Search';
import OpenInNew from '@mui/icons-material/OpenInNew';
import {
  getCustomerLendingAudit,
  CustomerLendingAuditResponse,
  CustomerLendingAuditRow,
} from '../../../services/request/Reports';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { PageHeader, DataToolbar } from '../../../shared-components/ui';

const fmt = (n: number) => formatCurrency(n, 'INR', true);

const CustomerLendingAudit: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<CustomerLendingAuditResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getCustomerLendingAudit();
      const payload = res.data;
      if (payload) {
        setData(payload);
      } else {
        setError('No data returned');
      }
    } catch (e: unknown) {
      const err = e as { message?: string };
      setError(err?.message || 'Failed to load.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = data?.rows ?? [];
  const needle = q.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!needle) return rows;
    return rows.filter((r: CustomerLendingAuditRow) => {
      const name = (r.borrowerName ?? '').toLowerCase();
      const cid = (r.customerId ?? '').toLowerCase();
      return name.includes(needle) || cid.includes(needle);
    });
  }, [rows, needle]);

  return (
    <Box>
      <PageHeader
        eyebrow="Lending · Reports"
        title="Customer lending audit"
        subtitle="One row per customer: outstanding on open debits, counts, and expected monthly interest."
        actions={
          <Button variant="contained" startIcon={<Refresh />} onClick={() => void load()} disabled={loading}>
            Refresh
          </Button>
        }
      />

      <DataToolbar>
        <TextField
          size="small"
          placeholder="Filter by name or customer ID"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 280 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </DataToolbar>

      {loading && <CircularProgress size={32} sx={{ display: 'block', mb: 2 }} />}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {data && !loading && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ columnGap: 2, rowGap: 1 }}>
              <Chip label={`${data.customerCount} customers`} color="primary" variant="outlined" />
              <Chip label={`Interest outstandings: ${fmt(data.totalInterestOutstanding)}`} color="info" variant="outlined" />
              <Chip label={`Short-term outstandings: ${fmt(data.totalShortTermOutstanding)}`} color="secondary" variant="outlined" />
              <Chip label={`Expected interest (month): ${fmt(data.totalExpectedInterestMonthly)}`} color="success" variant="outlined" />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {data.message}
            </Typography>
          </CardContent>
        </Card>
      )}

      {data && !loading && (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 640 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Customer ID</TableCell>
                <TableCell align="right">Open INTEREST debits</TableCell>
                <TableCell align="right">Debit (INTEREST) outstanding</TableCell>
                <TableCell align="right">Open short-term debits</TableCell>
                <TableCell align="right">Debit (short-term) outstanding</TableCell>
                <TableCell align="right">Expected interest (month)</TableCell>
                <TableCell>Profile</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((r: CustomerLendingAuditRow) => (
                <TableRow key={r.borrowerId} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                      <span>{r.borrowerName || '—'}</span>
                      {r.blacklisted && <Chip size="small" color="error" label="Blacklisted" variant="outlined" />}
                    </Stack>
                  </TableCell>
                  <TableCell>{r.customerId || '—'}</TableCell>
                  <TableCell align="right">{r.openInterestDebitCount}</TableCell>
                  <TableCell align="right">{fmt(r.interestOutstanding)}</TableCell>
                  <TableCell align="right">{r.openShortTermDebitCount}</TableCell>
                  <TableCell align="right">{fmt(r.shortTermOutstanding)}</TableCell>
                  <TableCell align="right">{fmt(r.expectedInterestMonthly)}</TableCell>
                  <TableCell>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => navigate(`/admin/customers/${r.borrowerId}/debits`)}
                      sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      Open debits
                      <OpenInNew sx={{ fontSize: 16 }} />
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {data && !loading && filtered.length === 0 && rows.length > 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          No rows match the filter.
        </Alert>
      )}
    </Box>
  );
};

export default CustomerLendingAudit;
